import { useEffect, useState } from 'react'
import { ArrowRight, Loader2 } from 'lucide-react'
import { Drawer, DrawerField, DrawerSection } from '@/shared/ui/Drawer'
import { ErrorStrip } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import { fmtDateTimeSec } from '@/shared/lib/format'
import { auditEntry } from './api'
import { OutcomeBadge } from './badges'
import type { AuditEntryResponse, FieldChange } from '@/shared/api/types'

/**
 * The row-click canvas.
 *
 * Opens from the summary row already in hand so there is content immediately,
 * then refetches the full entry — the list DTO and the detail DTO are the same
 * shape, but only the detail call is guaranteed to carry `userAgent` and the
 * complete `details` map.
 */
export function AuditDetailDrawer({
  entry, onClose,
}: { entry: AuditEntryResponse | null; onClose: () => void }) {
  const [full, setFull] = useState<AuditEntryResponse | null>(entry)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setFull(entry)
    setError(null)
    if (!entry) return
    let cancelled = false
    setLoading(true)
    auditEntry(entry.id)
      .then((e) => { if (!cancelled) setFull(e) })
      .catch((err) => { if (!cancelled) setError(explain(err)) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [entry])

  const e = full
  const changes = e?.changes ?? []

  return (
    <Drawer
      open={Boolean(entry)}
      onClose={onClose}
      title={e?.action ?? 'Audit entry'}
      subtitle={e ? fmtDateTimeSec(e.occurredAt) : undefined}
      right={e?.outcome ? <OutcomeBadge outcome={e.outcome} /> : undefined}
      width="lg"
    >
      <ErrorStrip message={error} />
      {loading && !e && (
        <div className="px-4 py-10 text-center text-erp-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
        </div>
      )}

      {e && (
        <>
          {/* The reason this drawer exists: what actually changed. */}
          <DrawerSection title={`Field changes${changes.length ? ` (${changes.length})` : ''}`}>
            {changes.length === 0 ? (
              <p className="px-4 py-3 text-erp-sm text-muted-foreground">
                This action did not record field-level changes. Login, export and read
                events have no before/after state to capture.
              </p>
            ) : (
              <div className="px-4 py-3 space-y-2">
                {changes.map((c, i) => <ChangeRow key={`${c.field}-${i}`} change={c} />)}
              </div>
            )}
          </DrawerSection>

          <DrawerSection title="Event">
            <DrawerField label="Action" value={<span className="font-medium">{e.action}</span>} />
            <DrawerField label="Category" value={e.category?.displayName ?? e.category?.code} />
            <DrawerField label="Outcome" value={e.outcome?.displayName ?? e.outcome?.code} />
            <DrawerField label="Occurred" value={fmtDateTimeSec(e.occurredAt)} />
          </DrawerSection>

          <DrawerSection title="Actor">
            {/* Labels are snapshots taken at write time — deliberately not
                re-resolved, so a later rename does not rewrite history. */}
            <DrawerField label="Name" value={e.actorLabel ?? <Muted>System</Muted>} />
            <DrawerField label="User ID" value={e.actorId} mono />
            <DrawerField label="IP address" value={e.ip} mono />
            <DrawerField label="User agent" value={e.userAgent} />
          </DrawerSection>

          <DrawerSection title="Target">
            <DrawerField label="Entity" value={e.entityLabel ?? <Muted>—</Muted>} />
            <DrawerField label="Type" value={e.entityType} mono />
            <DrawerField label="Entity ID" value={e.entityId} mono />
          </DrawerSection>

          <DrawerSection title="Trace">
            <DrawerField label="Entry ID" value={e.id} mono />
            <DrawerField
              label="Request ID"
              value={e.requestId
                ? <span className="font-mono text-erp-xs">{e.requestId}</span>
                : <Muted>—</Muted>}
            />
          </DrawerSection>

          <DrawerSection title="Details">
            {e.details && Object.keys(e.details).length > 0 ? (
              <div className="px-4 py-3">
                <pre className="text-erp-xs font-mono bg-muted/40 border border-border rounded-md p-3 overflow-x-auto whitespace-pre-wrap break-words">
                  {JSON.stringify(e.details, null, 2)}
                </pre>
              </div>
            ) : (
              <p className="px-4 py-3 text-erp-sm text-muted-foreground">No additional details.</p>
            )}
          </DrawerSection>
        </>
      )}
    </Drawer>
  )
}

function Muted({ children }: { children: React.ReactNode }) {
  return <span className="text-muted-foreground">{children}</span>
}

/**
 * from → to, side by side on desktop and stacked on a phone.
 * A null `from` means the field was unset, which is different from an empty
 * string, so it renders as an explicit "not set" rather than a blank cell.
 */
function ChangeRow({ change }: { change: FieldChange }) {
  return (
    <div className="border border-border rounded-md overflow-hidden">
      <div className="px-3 py-1.5 bg-muted/40 border-b border-border text-erp-xs font-semibold">
        {change.field}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] items-stretch">
        <Value value={change.from} tone="from" />
        <div className="hidden sm:flex items-center justify-center px-2 text-muted-foreground">
          <ArrowRight className="h-3.5 w-3.5" />
        </div>
        <Value value={change.to} tone="to" />
      </div>
    </div>
  )
}

function Value({ value, tone }: { value?: string | null; tone: 'from' | 'to' }) {
  const unset = value === null || value === undefined
  return (
    <div className={[
      'px-3 py-2 text-erp-sm min-w-0 break-words',
      tone === 'from'
        ? 'bg-red-50/50 text-red-900 dark:bg-red-950/20 dark:text-red-200 line-through decoration-red-400/60'
        : 'bg-emerald-50/50 text-emerald-900 dark:bg-emerald-950/20 dark:text-emerald-200',
      'sm:border-0 border-t border-border first:border-t-0',
    ].join(' ')}>
      {unset
        ? <span className="italic opacity-60 no-underline">not set</span>
        : value === ''
          ? <span className="italic opacity-60">empty</span>
          : value}
    </div>
  )
}
